import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormikProps } from 'formik';
import {
    asyncCreateTender,
    asyncUpdateTender,
    asyncGetTender,
} from '../../../../services/tender.service';
import { Tender } from '../../../../shared/types/tender';
import { ISelect } from '../../../../shared/types/shared';
import { ToastSwal } from '../../../components/general/toastSwals/toastSwal';
import { LICITACIONES_EDIT } from '../../../../routes/paths';

export const useClientFormModal = ({
    setIsOpen,
    threadId,
    isOnlyView,
    idTender,
    isOpen,
}: any) => {
    const [disabled, setDisabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const formikRef = useRef<FormikProps<Tender> | null>(null);
    const navigate = useNavigate();

    const modalTitle = isOnlyView
        ? 'Ver licitación'
        : idTender
            ? 'Editar licitación'
            : 'Crear licitación';

    const handleSetFormik = useCallback((formik: FormikProps<Tender>) => {
        formikRef.current = formik;
    }, []);

    useEffect(() => {
        const loadData = async () => {
            if (!isOpen) {
                formikRef.current?.resetForm();
                return;
            }

            if (!idTender) return;

            setLoading(true);
            setDisabled(true);

            try {
                const data = await asyncGetTender(idTender);

                if (data && isOpen) {
                    const sanitizedValues = {
                        ...data,
                        architectureManager:
                            ((data?.architectureManager as ISelect)
                                ?.id as string) ?? '',
                        accountManager:
                            ((data?.accountManager as ISelect)?.id as string) ??
                            '',
                        processType:
                            ((data?.processType as ISelect)?.id as string) ??
                            '',
                        deliveryMethod:
                            ((data?.deliveryMethod as ISelect)?.id as string) ??
                            '',
                        statusProcess:
                            ((data?.statusProcess as ISelect)?.id as string) ??
                            '',
                        projectId:
                            ((data?.project as ISelect)?.id as string) ?? '',
                    };

                    if (formikRef.current) {
                        formikRef.current.resetForm({
                            values: sanitizedValues,
                        });
                    } else {
                        setTimeout(() => {
                            formikRef.current?.resetForm({
                                values: sanitizedValues,
                            });
                        }, 200);
                    }
                }
            } catch (error) {
                ToastSwal.fire({
                    title: 'Error al cargar los datos',
                    icon: 'error',
                });
            } finally {
                setLoading(false);
                setDisabled(false);
            }
        };

        loadData();
    }, [isOpen, idTender]);

    const handleSubmit = async () => {
        const f = formikRef.current;
        if (!f || isOnlyView) return;

        await f.submitForm();
        if (Object.keys(f.errors).length > 0) return;

        setDisabled(true);
        try {
            const res = idTender
                ? await asyncUpdateTender(idTender, f.values)
                : await asyncCreateTender({ ...f.values, threadId });

            if (res) {
                ToastSwal.fire({
                    text: idTender
                        ? 'Licitación actualizada'
                        : 'Licitación iniciada exitosamente',
                    icon: 'success',
                    timer: 3000,
                });
                setIsOpen(false);
                if (!idTender) navigate(`${LICITACIONES_EDIT}/${res.id}`);
            }
        } catch (error) {
            ToastSwal.fire({
                title: idTender ? 'Error al actualizar' : 'Error al crear',
                icon: 'error',
            });
        } finally {
            setDisabled(false);
        }
    };

    return {
        setFormikTender: handleSetFormik,
        handleSubmit,
        disabled,
        loading,
        modalTitle,
    };
};
